import styled from '@emotion/styled';
import { Button, Form, Input, Segmented, Typography } from 'antd';
import App from 'antd/lib/app';
import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API, TOKEN_LS_PATH } from '../api';
import { useAppDispatch } from '../state';
import { loginAction } from '../state/auth/slice';
import { meActions } from '../state/me/slice';
import { playlistCurrentActions } from '../state/playlists/currentSlice';
import { playListAllActions } from '../state/playlists/listSlice';
import { Paths } from '../types/common';
import { TLoginForm } from '../types/users';

const Title = styled(Typography.Title)`
  color: ${({ theme }) => theme.COLORS.WHITE._100} !important;
  margin: 0 0 16px !important;
`;

const Mode = styled(Segmented)`
  width: 100%;
  margin-bottom: 20px;

  .ant-segmented-item {
    flex: 1;
    text-align: center;
  }
`;

const SubmitButton = styled(Button)`
  width: 100%;
`;

const RecoveryButton = styled(Button)`
  width: 100%;
  margin-top: 8px;
`;

type StudentAuthMode = 'login' | 'registration';

export const StudentLogin: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<TLoginForm>();
  const [mode, setMode] = useState<StudentAuthMode>('login');
  const [loading, setLoading] = useState(false);
  const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);

  const title = useMemo(
    () => (mode === 'login' ? 'Student login' : 'Student registration'),
    [mode]
  );

  const onSubmit = async (values: TLoginForm) => {
    setLoading(true);
    setShowPasswordRecovery(false);
    try {
      const response =
        mode === 'login'
          ? await API.auth.studentLogin(values)
          : await API.auth.studentRegistration(values);
      localStorage.setItem(TOKEN_LS_PATH, response.data.authorizationToken);
      dispatch(meActions.resetMe());
      dispatch(playlistCurrentActions.resetState());
      dispatch(playListAllActions.resetState());
      dispatch(loginAction(response.data.authorizationToken));
      message.success(
        mode === 'login'
          ? 'You are signed in'
          : 'Student account has been created'
      );
      navigate(Paths.PLAYLISTS, { replace: true });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message;
      if (
        mode === 'login' &&
        error?.response?.status === 401 &&
        errorMessage === 'Wrong credentials'
      ) {
        setShowPasswordRecovery(true);
      }
      message.error(
        errorMessage ??
          (mode === 'login'
            ? 'Unable to sign in as a student'
            : 'Unable to create student account')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={4}>{title}</Title>
      <Mode
        value={mode}
        options={[
          { label: 'Login', value: 'login' },
          { label: 'Registration', value: 'registration' },
        ]}
        onChange={(value) => {
          setMode(value as StudentAuthMode);
          setShowPasswordRecovery(false);
          form.resetFields();
        }}
      />
      <Form
        layout="vertical"
        requiredMark={false}
        form={form}
        onFinish={onSubmit}
      >
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Enter email' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input className="themed" style={{ width: 330 }} />
        </Form.Item>
        <Form.Item
          name="password"
          label="Password"
          rules={
            mode === 'registration'
              ? [
                  { required: true, message: 'Enter password' },
                  {
                    min: 8,
                    message: 'Password must be at least 8 characters',
                  },
                ]
              : [{ required: true, message: 'Enter password' }]
          }
        >
          <Input.Password
            className="themed"
            style={{ width: 330 }}
            autoComplete="current-password"
          />
        </Form.Item>
        <SubmitButton type="primary" htmlType="submit" loading={loading}>
          {mode === 'login' ? 'Login' : 'Create student account'}
        </SubmitButton>
        {showPasswordRecovery && (
          <RecoveryButton
            type="link"
            onClick={() => navigate(Paths.FORGOT_PASSWORD)}
          >
            Restore password
          </RecoveryButton>
        )}
      </Form>
    </div>
  );
};
